import formatEuros from '@UI/Helpers/formatEuros';
import formatAudioDuration from '@UI/Pages/Administration/AiCosts/Helpers/formatAudioDuration';
import formatCount from '@UI/Pages/Administration/AiCosts/Helpers/formatCount';
import SplitRow from '@UI/Pages/Administration/AiCosts/CostSummary/CostSplit/SplitRow';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

const splitStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

type Props = {
	readonly summary: AiCostSummaryView;
};

const resolveShare = (amount: number, total: number): number => (total > 0 ? (amount / total) * 100 : 0);

const describeAudios = (summary: AiCostSummaryView): string => {
	const { count, durationSeconds } = summary.audio;
	const audios = count === 1 ? '1 audio' : `${formatCount(count)} audios`;

	return `${audios} · ${formatAudioDuration(durationSeconds)} au total`;
};

const describeDocuments = (summary: AiCostSummaryView): string => {
	const { count, unpricedCount } = summary.documents;
	const documents = count === 1 ? '1 document généré' : `${formatCount(count)} documents générés`;

	return unpricedCount > 0 ? `${documents} · ${unpricedCount} sans tarif` : documents;
};

/** The share of the total spent on audios and on documents. */
const CostSplit: React.FC<Props> = ({ summary }) => {
	return (
		<div css={splitStyle}>
			<SplitRow
				label="dont audio"
				amount={formatEuros(summary.audio.costAmount)}
				percent={resolveShare(summary.audio.costAmount, summary.costAmount)}
				color={theme.aiCosts.audio.bar}
				ink={theme.aiCosts.audio.ink}
				detail={describeAudios(summary)}
			/>
			<SplitRow
				label="dont documents"
				amount={formatEuros(summary.documents.costAmount)}
				percent={resolveShare(summary.documents.costAmount, summary.costAmount)}
				color={theme.aiCosts.documents.bar}
				ink={theme.aiCosts.documents.ink}
				detail={describeDocuments(summary)}
			/>
		</div>
	);
};

export default CostSplit;
